import {
  MessageSquare, Users, Newspaper, FileText, CalendarDays, CalendarClock,
  Pin, Settings as SettingsIcon, Gamepad2, Share2, Receipt,
  LayoutDashboard, Clock, Smartphone,
} from 'lucide-react';
import { isStaffArea } from './areas';

/** Heutige Begrüßung + Vorname. */
export function dayGreeting(fullName, now = new Date()) {
  const first = String(fullName || '').trim().split(/\s+/)[0] || '';
  const h = now.getHours();
  const hello = h < 11 ? 'Guten Morgen' : h < 18 ? 'Guten Tag' : 'Guten Abend';
  return { hello, first, line: first ? `${hello}, ${first}!` : `${hello}!` };
}

export function initialsFromName(fullName) {
  const parts = String(fullName || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] || ''}${parts[parts.length - 1][0] || ''}`.toUpperCase();
}

export function todayIsoLocal(now = new Date()) {
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** Montag der Kalenderwoche (lokal). */
export function mondayOfWeek(isoOrDate) {
  const d = typeof isoOrDate === 'string'
    ? new Date(`${isoOrDate}T12:00:00`)
    : new Date(isoOrDate);
  const day = (d.getDay() + 6) % 7; // Mo=0
  d.setDate(d.getDate() - day);
  return todayIsoLocal(d);
}

export function addDaysIso(iso, delta) {
  const d = new Date(`${iso}T12:00:00`);
  d.setDate(d.getDate() + delta);
  return todayIsoLocal(d);
}

/**
 * Primär-Kacheln (Grid) und Sekundär-Liste je App-Bereich.
 * Lehrkraft: sechs große Kacheln, darunter Plusstunden, Kontakte, Einstellungen.
 */
export function homeTilesForArea(area, {
  unread = 0, slotRequests = 0, files = 0, plusHours = 0,
} = {}) {
  const staff = isStaffArea(area);
  const isVerwaltung = area === 'verwaltung';
  const isEltern = area === 'eltern';
  const isSchueler = area === 'schueler' || area === 'privat';

  const badge = (n) => (n > 0 ? n : null);

  if (staff) {
    const core = [
      {
        id: 'mobile-menu-news', icon: Newspaper, label: 'Aktuelles',
        sub: 'Neuigkeiten der Musikschule', to: '/m/news',
      },
      {
        id: 'mobile-menu-messages', icon: MessageSquare, label: 'Nachrichten',
        sub: unread ? `${unread} ungelesen` : 'Chats & Mitteilungen', to: '/m/messages',
        badge: badge(unread),
      },
      {
        id: 'mobile-menu-calendar', icon: CalendarDays, label: 'Terminplanung',
        sub: '10er / 8er / 4er Karten', to: '/m/terminplanung',
      },
      {
        id: 'mobile-menu-slot-requests', icon: CalendarClock, label: 'Terminanfragen',
        sub: slotRequests ? `${slotRequests} offen — Rückmeldung nötig` : 'Online-Anmeldung',
        to: '/m/terminanfragen', badge: badge(slotRequests),
      },
      {
        id: 'mobile-menu-documents', icon: FileText, label: 'Dokumente',
        sub: 'Von der Musikschule', to: '/m/documents',
      },
      {
        id: 'mobile-menu-files', icon: Share2, label: 'ShareSpace',
        sub: 'Materialien & Austausch', to: '/m/files', badge: badge(files),
      },
    ];
    return {
      primary: [
        ...(isVerwaltung ? [{
          id: 'mobile-menu-verwaltung', icon: LayoutDashboard, label: 'Desktop-Verwaltung',
          sub: 'Volles System', to: '/', external: true,
        }, {
          id: 'mobile-menu-app-accounts', icon: Smartphone, label: 'Companion öffnen',
          sub: 'Als Lehrer, Schüler oder Eltern', to: '/m/app-konten',
        }] : []),
        ...core,
      ],
      secondary: [
        {
          id: 'mobile-menu-plus-hours', icon: Clock, label: 'Plusstunden',
          sub: plusHours ? `${plusHours} neue Entscheidung` : 'Melden & Übersicht',
          to: '/m/plus-hours', badge: badge(plusHours),
        },
        {
          id: 'mobile-menu-contacts', icon: Users, label: 'Kontakte',
          sub: 'Schüler & Kollegen', to: '/m/contacts',
        },
        {
          id: 'mobile-menu-settings', icon: SettingsIcon, label: 'Einstellungen',
          sub: 'Kennungen, Konto, Push', to: '/m/settings',
        },
      ],
    };
  }

  const nachrichten = {
    id: 'mobile-menu-messages', icon: MessageSquare, label: 'Nachrichten',
    sub: unread ? `${unread} ungelesen` : 'Chats & Mitteilungen', to: '/m/messages',
    badge: badge(unread),
  };
  const aktuelles = {
    id: 'mobile-menu-news', icon: Newspaper, label: 'Aktuelles',
    sub: 'Neuigkeiten der Musikschule', to: '/m/news',
  };
  const dokumente = {
    id: 'mobile-menu-documents', icon: FileText, label: 'Dokumente',
    sub: 'Von der Musikschule', to: '/m/documents',
  };
  const share = {
    id: 'mobile-menu-files', icon: Share2, label: 'ShareSpace',
    sub: 'Materialien & Austausch', to: '/m/files', badge: badge(files),
  };
  const kontakte = {
    id: 'mobile-menu-contacts', icon: Users, label: 'Kontakte',
    sub: isEltern ? 'Schule & Lehrkräfte' : 'Lehrkräfte & Verwaltung', to: '/m/contacts',
  };
  const veranstaltungen = {
    id: 'mobile-menu-events', icon: CalendarDays, label: 'Veranstaltungen',
    sub: 'Konzerte & Termine', to: '/m/events',
  };

  return {
    primary: isEltern ? [
      aktuelles,
      nachrichten,
      {
        id: 'mobile-menu-invoices', icon: Receipt, label: 'Abrechnungen',
        sub: 'Rechnungen der Familie', to: '/m/abrechnungen',
      },
      share,
      kontakte,
      dokumente,
    ] : [
      nachrichten,
      aktuelles,
      dokumente,
      share,
      veranstaltungen,
      kontakte,
      ...(isSchueler ? [{
        id: 'mobile-menu-tools', icon: Gamepad2, label: 'Tools',
        sub: 'Quiz & Melodie (Beta)', to: '/m/tools',
      }] : []),
    ],
    secondary: [
      {
        id: 'mobile-menu-pinboard', icon: Pin, label: 'Pinnwand',
        sub: 'Schwarzes Brett', to: '/m/pinboard',
      },
      ...(!isSchueler ? [{
        id: 'mobile-menu-tools', icon: Gamepad2, label: 'Tools',
        sub: 'Musiktheorie (Beta)', to: '/m/tools',
      }] : []),
      {
        id: 'mobile-menu-settings', icon: SettingsIcon, label: 'Einstellungen',
        sub: 'Kennungen, Konto, Push', to: '/m/settings',
      },
    ],
  };
}
